import React from "react";
import { useTranslation } from "react-i18next";
import { CircularProgress } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { DialogFrame } from "../../../components/Dialogs/dialog-base";
import { PrimaryButton } from "../../../components/Buttons/PrimaryButton";
import { SecondaryButton } from "../../../components/Buttons/SecondaryButton";
import { Design } from "../../../../core/models/entities/Design";

/**
 * Multi-delete for the design list (flag REACT_APP_MULTI_DELETE_ENABLED).
 *
 * Nichlas' explicit requirement (19/8): deleting several designs at once MUST
 * ask TWICE. Step 1 is the neutral "what will happen" dialog (list of the
 * designs that WILL be deleted + how many are skipped because the API/UI rules
 * do not allow deleting them). Step 2 is the visibly sharper one — red border,
 * red confirm button, "cannot be undone" — and only ITS confirm button
 * actually deletes. Cancel/close anywhere before that deletes nothing.
 *
 * The component is purely presentational; the deletable/skipped split and the
 * sequential deletion live in useFunctionalListDesignViewModel so the rules
 * stay next to the single-delete they mirror.
 */
export interface MultiDeleteDialogsProps {
  /** 0 = closed, 1 = first (neutral) dialog, 2 = final (red) dialog. */
  step: 0 | 1 | 2;
  /** The selected designs that WILL be deleted (rules already applied). */
  deletable: Design[];
  /** Selected designs that are skipped (not deletable per the row rules). */
  skippedCount: number;
  /** True while the sequential deletion runs — locks every escape hatch. */
  isDeleting: boolean;
  /** Completed deletions so far (for the progress label on the button). */
  progress: number;
  onCancel: () => void;
  onContinue: () => void;
  onConfirm: () => void;
}

export function MultiDeleteDialogs({
  step,
  deletable,
  skippedCount,
  isDeleting,
  progress,
  onCancel,
  onContinue,
  onConfirm,
}: MultiDeleteDialogsProps) {
  const { t } = useTranslation();
  const count = deletable.length;

  // DialogFrame's chrome (incl. the corner X) closes via closeDialog — route
  // it to cancel, but make it a no-op mid-deletion so the run can't be
  // "closed away" half-done.
  const guardedCancel = () => {
    if (!isDeleting) onCancel();
  };

  const dialog1Vm = {
    isOpen: step === 1,
    openDialog: () => {},
    closeDialog: guardedCancel,
  };
  const dialog2Vm = {
    isOpen: step === 2,
    openDialog: () => {},
    closeDialog: guardedCancel,
  };

  return (
    <>
      {/* Dialog 1: neutral confirmation with the exact list of what happens */}
      <DialogFrame dialogOption={dialog1Vm} onClose={guardedCancel}>
        <div className={"flex flex-col space-y-4"}>
          <div className={"flex justify-center"}>
            <FontAwesomeIcon size={"3x"} icon={faTriangleExclamation} />
          </div>
          <p className={"text-center font-bold"}>
            {t("designListStyle.multiDeleteTitle", { count })}
          </p>
          {skippedCount > 0 && (
            <p className={"text-center text-xs text-gray-600"}>
              {t("designListStyle.multiDeleteSkipLine", {
                deletable: count,
                skipped: skippedCount,
              })}
            </p>
          )}
          <div
            className={
              "max-h-60 overflow-y-auto rounded-md border border-[#ECEEF9] divide-y divide-[#ECEEF9]"
            }
          >
            {deletable.map((design) => (
              <div
                key={design.id}
                className={"flex items-baseline gap-2 px-3 py-2 text-xs"}
              >
                <span className={"font-bold whitespace-nowrap"}>
                  {design.product?.mul ?? "-"}
                </span>
                <span
                  className={
                    "text-gray-600 overflow-hidden overflow-ellipsis whitespace-nowrap"
                  }
                >
                  {design.title}
                </span>
              </div>
            ))}
          </div>
          <div className={"flex justify-center space-x-4"}>
            <SecondaryButton width={"sm"} onClick={guardedCancel}>
              {t("cancel")}
            </SecondaryButton>
            <PrimaryButton width={"sm"} onClick={onContinue}>
              {t("designListStyle.multiDeleteContinue")}
            </PrimaryButton>
          </div>
        </div>
      </DialogFrame>

      {/* Dialog 2: the visibly sharper one — red border/button, executes */}
      <DialogFrame dialogOption={dialog2Vm} onClose={guardedCancel}>
        <div
          className={
            "flex flex-col space-y-4 rounded-lg border-2 border-[#E22F2F] p-4"
          }
        >
          <div className={"flex justify-center text-[#E22F2F]"}>
            <FontAwesomeIcon size={"3x"} icon={faTriangleExclamation} />
          </div>
          <p className={"text-center font-bold text-[#E22F2F]"}>
            {t("designListStyle.multiDeleteFinalTitle")}
          </p>
          <p className={"text-center whitespace-pre-line"}>
            {t("designListStyle.multiDeleteFinalWarning", { count })}
          </p>
          <div className={"flex justify-center space-x-4"}>
            <SecondaryButton
              width={"sm"}
              onClick={guardedCancel}
              disabled={isDeleting}
            >
              {t("cancel")}
            </SecondaryButton>
            {/* Solid red confirm; disabled + spinner while running is the
                double-click guard — a second click can never start a second
                run. */}
            <button
              className={
                "button-root px-4 bg-[#E22F2F] text-white border border-[#E22F2F] hover:brightness-95"
              }
              onClick={onConfirm}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <span className={"flex items-center gap-2"}>
                  <CircularProgress size={14} sx={{ color: "white" }} />
                  {t("designListStyle.multiDeleteDeleting", {
                    done: progress,
                    total: count,
                  })}
                </span>
              ) : (
                t("designListStyle.multiDeleteConfirm", { count })
              )}
            </button>
          </div>
        </div>
      </DialogFrame>
    </>
  );
}
